import { Box } from '@mantine/core'
import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { setMainScrollElement } from '@/lib/main-scroll-registry'

export type PageShellProps = {
  children: ReactNode
  /**
   * Заполнить высоту области под вкладками без внешней прокрутки страницы.
   * Внутренние блоки (списки, переписка) прокручиваются сами.
   */
  variant?: 'default' | 'fill'
}

/**
 * Оболочка контента под вкладками: полная ширина области под вкладками (без max-width как у Mantine Container).
 * Заголовок страницы — в верхней панели (`AppLayout`), по `pathname`.
 */
export function PageShell({ children, variant = 'default' }: PageShellProps) {
  const fill = variant === 'fill'
  const mainRef = useRef<HTMLDivElement>(null)

  /** Только default: здесь `overflow: auto` — основная вертикальная прокрутка под вкладками. */
  useLayoutEffect(() => {
    if (fill) return
    setMainScrollElement(mainRef.current)
    return () => {
      setMainScrollElement(null)
    }
  }, [fill])

  return (
    <Box
      ref={mainRef}
      component="main"
      {...(!fill ? { 'data-app-scroll-root': true } : {})}
      w="100%"
      maw="100%"
      miw={0}
      mx={0}
      /* Как у `AppShell.Header` (`paddingInline: var(--mantine-spacing-sm)`) */
      px={{ base: 'xs', sm: 'sm' }}
      py={fill ? 'xs' : { base: 'md', sm: 'lg' }}
      pb={fill ? 'xs' : { base: 'lg', sm: 'xl' }}
      style={
        fill
          ? {
              flex: '1 1 0%',
              minHeight: 0,
              height: '100%',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }
          : {
              flex: '1 1 0%',
              minHeight: 0,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              boxSizing: 'border-box',
            }
      }
    >
      {children}
    </Box>
  )
}
